import * as THREE from 'three';
import { mulberry32 } from './rng.js';

/* ------------------------------------------------------------------
   Laatoitettava value-noise/fBm-työkalupakki 2D-canvasille. Kaikki
   generoituu ajon aikana — peli ei lataa yhtään kuvatiedostoa.
   Pohjautuu 32_Call_of_Zulun src/world/materials.js:n yleiseen
   koneistoon (makeLattice/fbm/canvasFrom/texture/normalCanvas ovat
   sellaisenaan, eivät ole minkään tietyn materiaalin näköisiä).
------------------------------------------------------------------ */

const SIZE = 512;

/** Laatoittuva lattice-noise: ruudukko kietoutuu, joten tekstuuri toistuu saumattomasti. */
function makeLattice(res, rand) {
  const g = new Float32Array(res * res);
  for (let i = 0; i < g.length; i++) g[i] = rand();
  return { res, g };
}

function smooth(t) { return t * t * (3 - 2 * t); }

function sampleLattice(lat, x, y) {
  const { res, g } = lat;
  const fx = x * res, fy = y * res;
  const x0 = Math.floor(fx), y0 = Math.floor(fy);
  const tx = smooth(fx - x0), ty = smooth(fy - y0);
  const i0 = ((x0 % res) + res) % res, j0 = ((y0 % res) + res) % res;
  const i1 = (i0 + 1) % res, j1 = (j0 + 1) % res;
  const a = g[j0 * res + i0], b = g[j0 * res + i1];
  const c = g[j1 * res + i0], d = g[j1 * res + i1];
  return (a * (1 - tx) + b * tx) * (1 - ty) + (c * (1 - tx) + d * tx) * ty;
}

/** Moni-oktaavinen laatoittuva fBm — palauttaa Float32Array-korkeuskentän 0..1. */
export function fbm(seed, octaves = 5, baseRes = 4, gain = 0.5) {
  const rand = mulberry32(seed);
  const lats = [];
  for (let o = 0; o < octaves; o++) lats.push(makeLattice(baseRes * 2 ** o, rand));
  const out = new Float32Array(SIZE * SIZE);
  let min = Infinity, max = -Infinity;
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      let amp = 1, sum = 0, norm = 0;
      const u = x / SIZE, v = y / SIZE;
      for (let o = 0; o < octaves; o++) {
        sum += sampleLattice(lats[o], u, v) * amp;
        norm += amp;
        amp *= gain;
      }
      const val = sum / norm;
      out[y * SIZE + x] = val;
      if (val < min) min = val;
      if (val > max) max = val;
    }
  }
  const inv = 1 / Math.max(1e-6, max - min);
  for (let i = 0; i < out.length; i++) out[i] = (out[i] - min) * inv;
  return out;
}

export function canvasFrom(fn) {
  const c = document.createElement('canvas');
  c.width = c.height = SIZE;
  const ctx = c.getContext('2d');
  const img = ctx.createImageData(SIZE, SIZE);
  fn(img.data);
  ctx.putImageData(img, 0, 0);
  return c;
}

export function texture(canvas, { srgb = false, repeat = 1 } = {}) {
  const t = new THREE.CanvasTexture(canvas);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(repeat, repeat);
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  t.anisotropy = 8;
  t.generateMipmaps = true;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  return t;
}

/** Sobel-johdettu tangenttiavaruuden normaalikartta korkeuskentästä. */
export function normalCanvas(height, strength = 2.4) {
  return canvasFrom((d) => {
    const at = (x, y) => height[(((y % SIZE) + SIZE) % SIZE) * SIZE + (((x % SIZE) + SIZE) % SIZE)];
    for (let y = 0; y < SIZE; y++) {
      for (let x = 0; x < SIZE; x++) {
        const dx = (at(x + 1, y) - at(x - 1, y)) * strength;
        const dy = (at(x, y + 1) - at(x, y - 1)) * strength;
        const len = Math.hypot(dx, dy, 1);
        const i = (y * SIZE + x) * 4;
        d[i] = ((-dx / len) * 0.5 + 0.5) * 255;
        d[i + 1] = ((-dy / len) * 0.5 + 0.5) * 255;
        d[i + 2] = (1 / len * 0.5 + 0.5) * 255;
        d[i + 3] = 255;
      }
    }
  });
}

/* ------------------------------------------------------------------
   Esimerkkimateriaalit rakennettuna yllä olevan yleisen koneiston
   päälle. Nämä kaksi ovat MALLI parametrisoidusta proseduraalisesta
   PBR-tehtaasta — korvaa/laajenna oman pelin näköiseksi (multa, kangas,
   puu, lehti...), älä pidä nimiä "matta"/"karhea" kirjaimellisina.
------------------------------------------------------------------ */

/** Matta, vaihteleva pinta (esim. multa, kipsi, rappaus). */
export function proseduraalinenMatta({ seed = 1, tint = [0.5, 0.5, 0.5], damp = 0, repeat = 1 } = {}) {
  const blotch = fbm(seed, 5, 3, 0.55);
  const grain = fbm(seed + 101, 3, 64, 0.5);
  const streak = fbm(seed + 202, 4, 2, 0.6);

  const height = new Float32Array(SIZE * SIZE);
  for (let i = 0; i < height.length; i++) height[i] = blotch[i] * 0.55 + grain[i] * 0.45;

  const albedo = canvasFrom((d) => {
    for (let y = 0; y < SIZE; y++) {
      for (let x = 0; x < SIZE; x++) {
        const i = y * SIZE + x;
        const low = damp * Math.pow(y / SIZE, 1.6);
        let v = 0.62 + (blotch[i] - 0.5) * 0.34 + (grain[i] - 0.5) * 0.13;
        v *= 1 - low * 0.45 * (0.4 + streak[i] * 0.9);
        const o = i * 4;
        d[o] = Math.min(255, v * tint[0] * 255 * 1.9);
        d[o + 1] = Math.min(255, v * tint[1] * 255 * 1.9);
        d[o + 2] = Math.min(255, v * tint[2] * 255 * 1.9);
        d[o + 3] = 255;
      }
    }
  });

  const rough = canvasFrom((d) => {
    for (let y = 0; y < SIZE; y++) {
      for (let x = 0; x < SIZE; x++) {
        const i = y * SIZE + x;
        const low = damp * Math.pow(y / SIZE, 1.6);
        let r = 0.94 - (grain[i] - 0.5) * 0.18 - low * 0.55 * (0.3 + streak[i]);
        const o = i * 4;
        const b = Math.max(0, Math.min(255, r * 255));
        d[o] = d[o + 1] = d[o + 2] = b;
        d[o + 3] = 255;
      }
    }
  });

  const mat = new THREE.MeshStandardMaterial({
    map: texture(albedo, { srgb: true, repeat }),
    roughnessMap: texture(rough, { repeat }),
    normalMap: texture(normalCanvas(height, 2.0), { repeat }),
    metalness: 0.0,
    roughness: 1.0,
  });
  mat.normalScale.set(0.85, 0.85);
  return mat;
}

/** Kulunut/karhea pinta metallille, muoville tms. */
export function proseduraalinenKarhea({ seed = 1, color = 0x2a2d31, roughBase = 0.44, repeat = 2 } = {}) {
  const wear = fbm(seed, 5, 5, 0.55);
  const brush = fbm(seed + 7, 2, 128, 0.5);
  const height = new Float32Array(SIZE * SIZE);
  for (let i = 0; i < height.length; i++) height[i] = brush[i] * 0.7 + wear[i] * 0.3;

  const rough = canvasFrom((d) => {
    for (let i = 0; i < SIZE * SIZE; i++) {
      const w = wear[i] > 0.66 ? 0.85 : roughBase;
      const r = w - (brush[i] - 0.5) * 0.14;
      const o = i * 4;
      const b = Math.max(0, Math.min(255, r * 255));
      d[o] = d[o + 1] = d[o + 2] = b;
      d[o + 3] = 255;
    }
  });

  const metal = canvasFrom((d) => {
    for (let i = 0; i < SIZE * SIZE; i++) {
      const m = wear[i] > 0.66 ? 235 : 40;
      const o = i * 4;
      d[o] = d[o + 1] = d[o + 2] = m;
      d[o + 3] = 255;
    }
  });

  const mat = new THREE.MeshStandardMaterial({
    color: new THREE.Color(color),
    roughnessMap: texture(rough, { repeat }),
    metalnessMap: texture(metal, { repeat }),
    normalMap: texture(normalCanvas(height, 1.1), { repeat }),
    metalness: 1.0,
    roughness: 1.0,
  });
  mat.normalScale.set(0.5, 0.5);
  return mat;
}

/** Pehmeä pyöreä sprite — hehku/partikkeli/decal-pohja. */
export function glowTexture() {
  const S = 64;
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.25, 'rgba(255,255,255,0.65)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, S, S);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
